import type { Checkin, MyDay } from "@/features/attendance/schemas/attendance.schema";

/**
 * What today is, for the person looking at it.
 *
 * Four states, and the distinction that matters most is the first: a day
 * nobody is expected to work is not a day somebody has failed to start. The
 * old card said "You are checked out" on a Sunday, which reads as an
 * accusation on a day off.
 */
export type TodayState = "nonWorking" | "notStarted" | "working" | "done";

export interface Today {
  state: TodayState;
  /// Minutes worked so far, or for a finished day the total. Null when there
  /// is nothing to measure yet.
  workedMinutes: number | null;
  /// The shift window as text, or null when nobody assigned one.
  shiftWindow: string | null;
  /// Why the day is not worked, when it is not.
  nonWorkingReason: string | null;
  /// True when tapping today produces raw evidence but no attendance record,
  /// because the day is not worked and the shift does not derive days on
  /// non-working days. The tap is still worth making and still kept.
  tapWillNotCount: boolean;
}

function minutesBetween(from: Date, to: Date): number {
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 60_000));
}

/**
 * Elapsed time since checking in, or across a finished day.
 *
 * Deliberately from the raw instants rather than the wall-clock strings: a
 * night shift's check-in and check-out fall on different dates, and
 * subtracting "22:48" from "06:10" as text gives a negative day.
 */
export function workedMinutes(last: Checkin | null, now: Date): number | null {
  if (last === null) return null;
  if (last.logType === "check_in") return minutesBetween(new Date(last.time), now);
  return null;
}

export function describeToday(
  myDay: MyDay | undefined,
  last: Checkin | null,
  nextLogType: "check_in" | "check_out" | undefined,
  now: Date,
): Today {
  const shiftWindow = myDay?.shift ? `${myDay.shift.startTime} to ${myDay.shift.endTime}` : null;
  const nonWorkingReason = myDay?.nonWorkingReason ?? null;

  // A non-working day wins over everything. Somebody who taps in on a public
  // holiday is doing something unusual, and the card should say the day is a
  // holiday rather than pretend it is an ordinary shift.
  if (nonWorkingReason !== null) {
    return {
      state: "nonWorking",
      workedMinutes: null,
      shiftWindow,
      nonWorkingReason,
      tapWillNotCount: myDay?.marksAttendanceOnHolidays !== true,
    };
  }

  const working = nextLogType === "check_out";
  return {
    state: working ? "working" : last === null ? "notStarted" : "done",
    workedMinutes: working ? workedMinutes(last, now) : null,
    shiftWindow,
    nonWorkingReason: null,
    tapWillNotCount: false,
  };
}

/// "4h 32m", or "18m" under the hour. Never "0h 18m", which reads like a
/// broken clock.
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest}m`;
  return `${hours}h ${String(rest).padStart(2, "0")}m`;
}

/**
 * How late an arrival was, in minutes, or null when it was not late.
 *
 * Computed from the shift's own start rather than trusting the stored flag
 * alone, because "Late" tells somebody nothing they can argue with and "Late
 * by 48m" tells them exactly what happened.
 */
export function minutesLate(inTimeLocal: string | null, shiftStart: string | null): number | null {
  if (inTimeLocal === null || shiftStart === null) return null;
  const arrived = toMinutes(inTimeLocal);
  const due = toMinutes(shiftStart);
  if (arrived === null || due === null) return null;
  const late = arrived - due;
  return late > 0 ? late : null;
}

function toMinutes(time: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match?.[1] || !match[2]) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}
